import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment, useState } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyL,
  BodyM,
  BodyS,
  HeadlineL,
  HeadlineM,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { TermsDialog } from '@sorare/core/src/components/conversionCredit/TermsDialog';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { fantasy, navLabels } from '@sorare/core/src/lib/glossary';

import { ComposeRules } from '@sorare/gameplay/src/components/leaderboard/ComposeRules';
import { isFixtureOpened } from '@sorare/gameplay/src/lib/so5';

import { Leaderboard } from 'components/campaigns/Leaderboard';
import { NextAction } from 'components/campaigns/NextAction';

import { CampaignPage_so5Root } from './__generated__/index.graphql';

const Root = styled(Container)`
  padding-top: var(--triple-unit);
  padding-bottom: var(--triple-unit);
`;
const Sticky = styled.footer`
  position: sticky;
  bottom: var(--unit);
`;
const Cover = styled.div`
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  width: 100%;
  aspect-ratio: 884/430;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center bottom;
`;
const Content = styled(Horizontal)`
  align-items: flex-start;
  > *:first-child {
    flex: 1;
  }
`;
const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--c-nd-150);
`;
const Aside = styled.div`
  max-width: 320px;
  width: 100%;
`;
const StyledButtonBase = styled(ButtonBase)`
  text-decoration: underline;
  font-weight: var(--t-bold);
`;
const StyledLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
  font-weight: var(--t-bold);
`;

enum MobileTabs {
  OVERVIEW = 'OVERVIEW',
  ENTRIES = 'ENTRIES',
  RULES = 'RULES',
}

const tabMessages: Record<MobileTabs, MessageDescriptor> = {
  [MobileTabs.OVERVIEW]: navLabels.overview,
  [MobileTabs.ENTRIES]: {
    id: 'campaigns.$slug.entries',
    defaultMessage: 'Entries',
  },
  [MobileTabs.RULES]: fantasy.rules,
};

type Props = {
  so5: CampaignPage_so5Root;
  composeTeamUrl?: string;
};
export const CampaignPage = ({ so5, composeTeamUrl }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const isLaptopAndAbove = useIsLaptop();
  const { campaign } = so5;

  const [activeTab, setActiveTab] = useState<MobileTabs>(MobileTabs.OVERVIEW);
  const [termsOpen, setTermsOpen] = useState(false);

  if (!campaign) {
    return null;
  }

  const { so5Leaderboards } = campaign;

  const nextUpcomingSo5Leaderboard = so5Leaderboards?.find(({ so5Fixture }) =>
    isFixtureOpened(so5Fixture)
  );

  const so5LeaderboardForRules =
    nextUpcomingSo5Leaderboard || so5Leaderboards?.[0];

  const featuredGameWeeks = so5Leaderboards && so5Leaderboards.length > 1 && (
    <Vertical gap={0}>
      <HeadlineM as="p">
        <FormattedMessage
          id="campaigns.$slug.featuredGameWeeks"
          defaultMessage="Featured Game Weeks"
        />
      </HeadlineM>
      <Horizontal>
        {so5Leaderboards.map((so5Leaderboard, i) => (
          <Fragment key={so5Leaderboard.slug}>
            <BodyM as="p" color="var(--c-nd-600)">
              <DateRange {...so5Leaderboard.so5Fixture} />
            </BodyM>
            {i !== so5Leaderboards.length - 1 && (
              <BodyM as="p" color="var(--c-nd-600)">
                •
              </BodyM>
            )}
          </Fragment>
        ))}
      </Horizontal>
    </Vertical>
  );

  const sections = campaign.sections.map((section, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <Vertical gap={3} key={i}>
      {(isLaptopAndAbove || i !== 0) && <Separator />}
      <HeadlineM as="p">{section.title}</HeadlineM>
      {section.items.map((item, j) => (
        // eslint-disable-next-line react/no-array-index-key
        <Horizontal key={j} gap={1.5}>
          {item.iconUrl && (
            <Img src={item.iconUrl} alt="" width="56" height="72" />
          )}
          <Vertical gap={0}>
            <BodyS as="p" bold>
              {item.title}
            </BodyS>
            <BodyS as="p" color="var(--c-nd-600)">
              {item.subtitle}
            </BodyS>
          </Vertical>
        </Horizontal>
      ))}
    </Vertical>
  ));

  const rules = !!so5Leaderboards?.length && (
    <>
      {isLaptopAndAbove && <Separator />}
      <Vertical gap={3}>
        <Vertical gap={2}>
          {isLaptopAndAbove && (
            <HeadlineM as="p">
              <FormattedMessage {...fantasy.rules} />
            </HeadlineM>
          )}
          <BodyM as="p" color="var(--c-nd-600)">
            <FormattedMessage
              id="campaigns.$slug.composeATeamIncluding5Cards"
              defaultMessage="Compose a team of 5 cards including:"
            />
          </BodyM>
          {so5LeaderboardForRules && (
            <ComposeRules so5Leaderboard={so5LeaderboardForRules} />
          )}
        </Vertical>
        {(campaign.faqUrl || campaign.termsFilename) && (
          <Vertical>
            {campaign.faqUrl && (
              <BodyS as="p" color="var(--c-nd-600">
                <FormattedMessage
                  id="campaigns.$slug.forMoreInformationFAQ"
                  defaultMessage="For more information, visit our <link>FAQ</link>"
                  values={{
                    link: (...children) => (
                      <StyledLink
                        to={campaign.faqUrl!}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </StyledLink>
                    ),
                  }}
                />
              </BodyS>
            )}
            {campaign.termsFilename && (
              <>
                {termsOpen && (
                  <TermsDialog
                    fileName={campaign.termsFilename}
                    onClose={() => setTermsOpen(false)}
                  />
                )}
                <BodyS as="p" color="var(--c-nd-600">
                  <FormattedMessage
                    id="campaigns.$slug.viewTermsAndConditions"
                    defaultMessage="View <link>Terms and Conditions</link>"
                    values={{
                      link: (...children) => (
                        <StyledButtonBase
                          type="button"
                          onClick={() => setTermsOpen(true)}
                        >
                          {children}
                        </StyledButtonBase>
                      ),
                    }}
                  />
                </BodyS>
              </>
            )}
          </Vertical>
        )}
      </Vertical>
    </>
  );

  const leaderboards = so5Leaderboards?.map((so5Leaderboard, i, array) => (
    <Leaderboard
      key={so5Leaderboard.slug}
      so5Leaderboard={so5Leaderboard}
      previousLeaderboard={array[i - 1]}
      composeTeamUrl={composeTeamUrl}
      displayName={i === 0 ? campaign.title : undefined}
    />
  ));

  const nextAction = currentUser && (
    <NextAction so5Campaign={campaign} composeTeamUrl={composeTeamUrl} />
  );

  const title = <HeadlineL as="p">{campaign.title}</HeadlineL>;

  const description = (
    <BodyL as="p" color="var(--c-nd-600)">
      {campaign.description}
    </BodyL>
  );

  return (
    <>
      <Root>
        <Vertical gap={3}>
          {isLaptopAndAbove && title}
          {campaign.pictureUrl && (
            <Cover
              style={{
                backgroundImage: `url(${campaign.pictureUrl})`,
              }}
            />
          )}
          {isLaptopAndAbove ? (
            <Content gap={3}>
              <Vertical gap={3}>
                {description}
                {featuredGameWeeks}
                {sections}
                {rules}
              </Vertical>
              {!!so5Leaderboards?.length && (
                <Aside>
                  <Vertical gap={3}>
                    {nextAction}
                    {leaderboards}
                  </Vertical>
                </Aside>
              )}
            </Content>
          ) : (
            <Vertical gap={3}>
              <Vertical>
                {title}
                {description}
              </Vertical>
              <TabBar value={activeTab} variant="flat" fullWidth>
                {Object.values(MobileTabs).map(tab => (
                  <Tab key={tab} value={tab} onClick={() => setActiveTab(tab)}>
                    <FormattedMessage {...tabMessages[tab]} />
                  </Tab>
                ))}
              </TabBar>

              {activeTab === MobileTabs.OVERVIEW && sections}
              {activeTab === MobileTabs.ENTRIES && leaderboards}
              {activeTab === MobileTabs.RULES && rules}
            </Vertical>
          )}
        </Vertical>
      </Root>
      {!isLaptopAndAbove && <Sticky>{nextAction}</Sticky>}
    </>
  );
};

CampaignPage.fragments = {
  so5Root: gql`
    fragment CampaignPage_so5Root on So5Root {
      campaign(slug: $slug) {
        slug
        title
        description
        pictureUrl
        termsFilename
        faqUrl
        sections {
          iconUrl
          title
          items {
            iconUrl
            title
            subtitle
          }
        }
        so5Leaderboards {
          slug
          so5Fixture {
            slug
            startDate
            endDate
            ...isFixtureOpened_so5Fixture
          }
          ...ComposeRules_so5Leaderboard
          ...Leaderboard_so5Leaderboard
          ...Leaderboard_previousSo5Leaderboard
        }
        ...NextAction_so5Campaign
      }
    }
    ${ComposeRules.fragments.so5Leaderboard}
    ${Leaderboard.fragments.so5Leaderboard}
    ${Leaderboard.fragments.previousSo5Leaderboard}
    ${NextAction.fragments.so5Campaign}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<CampaignPage_so5Root>,
};
